import React from "react";
import { useTranslation } from "react-i18next";
import LegalPageShell from "../../components/LegalPageShell";

export default function DataProcessingAddendum() {
  const { t } = useTranslation(["legalDpa", "common", "legal"]);

  const sections = [
    { id: "introduction", label: t("introduction", "Introduction") },
    { id: "purpose", label: t("purpose", "Purpose") },
    { id: "definitions", label: t("definitions", "Definitions") },
    { id: "roles", label: t("roles", "Roles") },
    { id: "processing-purposes", label: t("processingPurposes", "Processing Purposes") },
    { id: "categories-data", label: t("categoriesOfDataAndDataSubjects", "Categories of Data & Data Subjects") },
    { id: "subprocessing", label: t("subprocessing", "Subprocessing") },
    { id: "security-measures", label: t("securityMeasures", "Security Measures") },
    { id: "data-subject-rights", label: t("dataSubjectRights", "Data Subject Rights") },
    { id: "international-transfers", label: t("internationalTransfers", "International Transfers") },
    { id: "deletion-return", label: t("deletionAndReturn", "Deletion & Return") },
    { id: "audit-rights", label: t("auditRights", "Audit Rights") },
    { id: "liability", label: t("liability", "Liability") },
  ];

  return (
<LegalPageShell
        title={t("title", "Data Processing Addendum")}
        subtitle={t("subtitle", "Data processing terms for Orrith services.")}
        effectiveDate={`Effective: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
        sections={sections}
        namespace="legalDpa"
      >
      <section id="introduction" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("introduction", "Introduction")}
        </h2>
        <p className="legal-prose">
          {t("dpaIntro", "This Data Processing Addendum (\"DPA\") forms part of the agreement between you (\"Customer\" or \"Controller\") and Orrith - Cycle of Certainty (\"Processor\") accessible at orrith.me. It supplements the Terms of Service and Privacy Policy and applies when Processor processes Personal Data on Customer's behalf. By using Orrith's services involving personal data processing, you agree to this DPA.")}
        </p>
      </section>

      <section id="purpose" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("purpose", "1. Purpose")}
        </h2>
        <p className="legal-prose">
          {t("purposeDesc", "This DPA sets out the scope, nature, and purpose of processing, the duration, types of Personal Data, and obligations of both parties. It is designed to comply with applicable data protection laws including the EU GDPR, UK GDPR, and similar regulations worldwide.")}
        </p>
      </section>

      <section id="definitions" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("definitions", "2. Definitions")}
        </h2>
        <p className="legal-prose">
          {t("definitionsDesc", "Terms used in this DPA have the meanings given in applicable data protection laws. \"Personal Data\" means any information relating to an identified or identifiable natural person (\"Data Subject\"). \"Processing\" means any operation performed on Personal Data. \"Subprocessor\" means a third party engaged by Processor to process Personal Data on behalf of Customer.")}
        </p>
      </section>

      <section id="roles" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("roles", "3. Roles")}
        </h2>
        <p className="legal-prose">
          {t("rolesDesc", "The Customer is the Controller, and Orrith - Cycle of Certainty is the Processor (or Subprocessor when acting under Customer's instruction). Each party will comply with its obligations under applicable data protection law with respect to Personal Data processed under this DPA.")}
        </p>
      </section>

      <section id="processing-purposes" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("processingPurposes", "4. Processing Purposes")}
        </h2>
        <p className="legal-prose mb-4">
          {t("processingPurposesDesc", "Processor will process Personal Data only for the purposes of providing the Service to Customer, including:")}
        </p>
        <ul className="legal-list">
          <li>{t("processingPurposesItems.auth", "User authentication and account management")}</li>
          <li>{t("processingPurposesItems.marketplace", "Marketplace operations and transaction processing")}</li>
          <li>{t("processingPurposesItems.analytics", "Analytics and telemetry ingestion")}</li>
          <li>{t("processingPurposesItems.notifications", "Notifications and customer support")}</li>
          <li>{t("processingPurposesItems.compliance", "Legal compliance and security monitoring")}</li>
        </ul>
      </section>

      <section id="categories-data" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("categoriesOfDataAndDataSubjects", "5. Categories of Data & Data Subjects")}
        </h2>
        <p className="legal-prose mb-4">
          {t("categoriesDataTypes", "Types of Personal Data processed:")}
        </p>
        <ul className="legal-list">
          <li>{t("categoriesData.identifiers", "Identifiers: name, email, username")}</li>
          <li>{t("categoriesData.contact", "Contact data: phone, address, location (if consented)")}</li>
          <li>{t("categoriesData.transaction", "Transaction data: offers, orders, payments")}</li>
          <li>{t("categoriesData.device", "Device and usage data: IP address, browser type, access logs")}</li>
          <li>{t("categoriesData.telemetry", "Sensor telemetry (if opted-in): environmental sensor readings")}</li>
        </ul>

        <p className="legal-prose mt-4">
          {t("categoriesDataSubjects", "Data Subjects: end users, employees, agents, contractors, beneficiaries, and third parties whose data is processed through the platform.")}
        </p>
      </section>

      <section id="subprocessing" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("subprocessing", "6. Subprocessing")}
        </h2>
        <p className="legal-prose mb-4">
          {t("subprocessingDesc", "Customer authorizes Processor to engage Subprocessors to provide the Service. Current Subprocessors include:")}
        </p>
        <ul className="legal-list">
          <li>{t("subprocessors.firebase", "Firebase (Google LLC) — authentication, database, storage, functions")}</li>
          <li>{t("subprocessors.imageUpload", "Configured image upload service — listing photo processing")}</li>
          <li>{t("subprocessors.translation", "Configured translation endpoint — auto-translation (when enabled)")}</li>
        </ul>
        <p className="legal-prose mt-4">
          {t("subprocessingObligations", "Processor will ensure Subprocessors are bound by written agreements imposing the same data protection obligations as this DPA. Processor remains liable for Subprocessor compliance. A non-exhaustive list of Subprocessors is available on request to legal@orrith.me.")}
        </p>
      </section>

      <section id="security-measures" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("securityMeasures", "7. Security Measures")}
        </h2>
        <p className="legal-prose mb-4">
          {t("securityMeasuresDesc", "Processor will implement appropriate technical and organizational measures to ensure a level of security appropriate to the risk, including:")}
        </p>
        <ul className="legal-list">
          <li>{t("securityMeasuresItems.encryption", "Encryption in transit (TLS 1.2+) for all data transmission")}</li>
          <li>{t("securityMeasuresItems.accessControls", "Logical access controls with role-based permissions")}</li>
          <li>{t("securityMeasuresItems.anonymization", "Anonymization and pseudonymization where possible")}</li>
          <li>{t("securityMeasuresItems.incidentResponse", "Incident response procedures and breach notification processes")}</li>
          <li>{t("securityMeasuresItems.audits", "Regular security assessments and Firebase infrastructure certifications")}</li>
        </ul>
        <p className="legal-prose mt-4">
          {t("securityMeasuresSecurityPage", "See our Security page at orrith.me/legal/security for additional details on operational safeguards.")}
        </p>
      </section>

      <section id="data-subject-rights" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("dataSubjectRights", "8. Data Subject Rights")}
        </h2>
        <p className="legal-prose mb-4">
          {t("dataSubjectRightsDesc", "Processor will assist Customer in responding to Data Subject requests to the extent feasible and legally permitted:")}
        </p>
        <ul className="legal-list">
          <li>{t("dataSubjectRightsItems.access", "Access: Confirmation of processing and copy of Personal Data")}</li>
          <li>{t("dataSubjectRightsItems.rectification", "Rectification: Correction of inaccurate or incomplete data")}</li>
          <li>{t("dataSubjectRightsItems.erasure", "Erasure: Deletion of Personal Data where legally required")}</li>
          <li>{t("dataSubjectRightsItems.portability", "Portability: Structured, commonly used, machine-readable format")}</li>
          <li>{t("dataSubjectRightsItems.restriction", "Restriction: Limitation of processing under certain conditions")}</li>
          <li>{t("dataSubjectRightsItems.objection", "Objection: Right to object to processing for direct marketing")}</li>
        </ul>
        <p className="legal-prose mt-4">
          {t("dataSubjectRightsCustomerResponsible", "Customer remains primarily responsible for the management and verification of Data Subject requests. Processor will provide reasonable assistance within 30 days of Customer's request.")}
        </p>
      </section>

      <section id="international-transfers" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("internationalTransfers", "9. International Transfers")}
        </h2>
        <p className="legal-prose">
          {t("internationalTransfersDesc", "Where Personal Data is transferred outside the European Economic Area, United Kingdom, or other jurisdictions with adequacy decisions, Processor will implement appropriate safeguards such as Standard Contractual Clauses (SCCs), binding corporate rules, or other mechanisms permitted under applicable law. Firebase data centers are located globally; data residency options are configured per deployment.")}
        </p>
      </section>

      <section id="deletion-return" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("deletionAndReturn", "10. Deletion & Return")}
        </h2>
        <p className="legal-prose">
          {t("deletionReturnDesc", "Upon termination or expiration of services, Processor will, at Customer's choice, delete or return Personal Data, unless retention is required by applicable law. Processor will certify deletion on request. Deletion includes removal from active systems and backups within 90 days, subject to legal hold obligations.")}
        </p>
      </section>

      <section id="audit-rights" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("auditRights", "11. Audit Rights")}
        </h2>
        <p className="legal-prose">
          {t("auditRightsDesc", "Customer may audit Processor's compliance via agreed questionnaires, certifications (e.g., SOC 2, ISO 27001 reports), and/or reasonable on-site audits conducted by mutually agreed third parties subject to confidentiality protections. Audit requests must be reasonable in scope and frequency (not more than once per 12 months absent cause).")}
        </p>
      </section>

      <section id="liability" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("liability", "12. Liability")}
        </h2>
        <p className="legal-prose">
          {t("liabilityDesc", "The parties' liability for breaches of this DPA will be subject to the limitations set out in the Terms of Service, except to the extent prohibited by applicable law. Neither party excludes liability for fraud, death, or personal injury caused by negligence.")}
        </p>

        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-neutral-800">
          <p className="legal-prose">
            {t("dpaContact", "To request our list of subprocessors, Standard Contractual Clauses, or for DPA inquiries, contact ")}<strong>legal@orrith.me</strong>.
          </p>
        </div>
      </section>
    </LegalPageShell>
  );
}